import { useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePage } from "@inertiajs/react";
import AdCampaignFieldList from "@components/ads/forms/shared/AdCampaignFieldList";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@components/ui/field";
import { useAdsChatSubmit } from "@components/ads/hooks";
import { useFormRegistration } from "@hooks/useFormRegistration";
import { useDebouncedSave } from "@hooks/useDebouncedSave";
import { Ads, generateUUID } from "@shared";
import { Lock, LockOpen, Plus } from "lucide-react";
import InfoTooltip from "@components/ui/info-tooltip";
import RefreshSuggestionsButton from "../shared/RefreshSuggestionsButton";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import type { UpdateCampaignRequestBody } from "@rails_api_base";

const STRUCTURED_SNIPPET_CATEGORIES = Ads.StructuredSnippetCategoryKeys.map((key) => ({
  value: key,
  label: Ads.StructuredSnippetCategories[key].key,
}));

interface AdsPageProps {
  campaign?: { id: number; [key: string]: unknown };
  structured_snippet?: {
    category?: string;
    values?: string[];
    locked?: boolean;
  } | null;
  [key: string]: unknown;
}

function transformSnippets(data: Ads.StructuredSnippetsOutput): Partial<UpdateCampaignRequestBody> | null {
  const values = data.details?.filter((d) => d.text?.trim()).map((d) => d.text) ?? [];

  if (!data.category && values.length === 0) return null;

  if (values.length === 0) {
    return { structured_snippet: { _destroy: true } } as any;
  }

  return {
    structured_snippet: {
      category: data.category,
      values,
    },
  };
}

export default function StructuredSnippetsForm() {
  const page = usePage<AdsPageProps>();
  const savedSnippet = page.props.structured_snippet;
  const campaignId = page.props.campaign?.id;
  const submit = useAdsChatSubmit();

  const category = savedSnippet?.category;
  const savedValues = savedSnippet?.values || [];
  const hasSavedValues = savedValues.length > 0;
  const savedLocked = savedSnippet?.locked ?? false;

  // Structured snippet values are strings in a JSONB column, not individual records —
  // use index-based IDs since there are no Rails record IDs per value.
  // locked state is at the snippet level (not per-value), so all details share it.
  const details: Ads.Asset[] = hasSavedValues
    ? savedValues.map((text, i) => {
        const id = `snippet-${i}`;
        return {
          id,
          text,
          locked: savedLocked,
        };
      })
    : Array.from({ length: Ads.DefaultNumAssets.structuredSnippets }, (_, i) => ({
        id: `placeholder-snippet-${i}`,
        text: "",
        locked: false,
      }));

  const prevFingerprintRef = useRef("");

  const methods = useForm<Ads.StructuredSnippetsOutput>({
    resolver: zodResolver(Ads.StructuredSnippetsFormSchema) as any,
    mode: "onChange",
    defaultValues: {
      category: category as Ads.StructuredSnippetCategoryKey,
      details,
    },
  });

  const { save, saveNow, cancel } = useDebouncedSave({ campaignId });

  const getData = useCallback((): Partial<UpdateCampaignRequestBody> | null => {
    return transformSnippets(methods.getValues());
  }, [methods]);

  // Sync Inertia props → RHF when content changes (after router.reload())
  const currentFingerprint = JSON.stringify(details.map((d) => [d.id, d.text]));
  if (currentFingerprint !== prevFingerprintRef.current) {
    methods.reset({ category: category as Ads.StructuredSnippetCategoryKey, details });
    prevFingerprintRef.current = currentFingerprint;
  }

  const handleCategoryChange = useCallback(
    (value: string) => {
      methods.setValue("category", value as Ads.StructuredSnippetCategoryKey);
      const apiData = transformSnippets(methods.getValues());
      if (apiData) saveNow(apiData);
    },
    [methods, saveNow]
  );

  // Structured snippets lock at the group level — Rails stores one `locked`
  // boolean per snippet row, and Google renders the whole list as a cohesive
  // unit. Flipping the group flips every detail's local `locked` flag so the
  // shape matches storage and downstream merge logic short-circuits cleanly.
  // Awaits saveNow so the PATCH lands before any refresh can fire (loadAssets
  // reads Rails on every turn; the lock must be there when it does).
  const isGroupLocked = (methods.watch("details") as Ads.Asset[] | undefined)?.some(
    (d) => d.locked,
  ) ?? false;

  const handleGroupLockToggle = useCallback(async () => {
    const current = methods.getValues("details") as Ads.Asset[];
    const anyHasText = current.some((d) => d.text?.trim());

    // Don't allow locking an empty snippet group — there's nothing to lock.
    if (!isGroupLocked && !anyHasText) {
      methods.setError("details" as any, {
        type: "manual",
        message: "Add at least one value before locking.",
      });
      return;
    }

    const nextLocked = !isGroupLocked;
    const updated = current.map((d) => ({ ...d, locked: nextLocked }));
    methods.reset({
      category: methods.getValues("category"),
      details: updated,
    });
    const apiData = transformSnippets({ ...methods.getValues(), details: updated });
    if (apiData) await saveNow(apiData);
  }, [methods, saveNow, isGroupLocked]);

  const handleAddDetail = useCallback(() => {
    const current = methods.getValues("details") as Ads.Asset[];
    const newDetail: Ads.Asset = { id: generateUUID(), text: "", locked: true };
    const updated = [...current, newDetail];
    methods.reset({
      category: methods.getValues("category"),
      details: updated,
    });
    const apiData = transformSnippets(methods.getValues());
    if (apiData) saveNow(apiData);
  }, [methods, saveNow]);

  const handleDeleteDetail = useCallback(
    (index: number) => {
      const current = methods.getValues("details") as Ads.Asset[];
      const minCount = Ads.DefaultNumAssets.structuredSnippets ?? 3;

      let updated: Ads.Asset[];
      if (current.length > minCount) {
        // Above minimum: remove the field entirely
        updated = current.filter((_, i) => i !== index);
      } else {
        // At minimum: clear the text but keep the slot
        updated = current.map((a, i) =>
          i === index ? { ...a, id: generateUUID(), text: "", locked: false } : a
        );
      }

      methods.reset({
        category: methods.getValues("category"),
        details: updated,
      });

      // Only save if there are still non-empty values — don't send _destroy
      // when the user is just clearing individual slots at the minimum count
      const hasFilledValues = updated.some((d) => d.text?.trim());
      if (hasFilledValues) {
        const apiData = transformSnippets({ ...methods.getValues(), details: updated });
        if (apiData) saveNow(apiData);
      }
    },
    [methods, saveNow]
  );

  const handleInputChange = useCallback(
    (index: number, input: string) => {
      const current = methods.getValues("details") as Ads.Asset[];
      const updated = current.map((d, i) => (i === index ? { ...d, text: input } : d));
      methods.reset({
        category: methods.getValues("category"),
        details: updated,
      });
      const apiData = transformSnippets({ ...methods.getValues(), details: updated });
      if (apiData) save(apiData);
    },
    [methods, save]
  );

  // Flush pending edits (debounced or in-flight) to Rails before firing the
  // refresh intent. The graph's loadAssets node reads the authoritative state
  // from Rails on every turn; this await closes the race where a user edits
  // and immediately clicks refresh before the PATCH has landed.
  //
  // If the group is locked, refresh is a no-op — Google renders the snippet
  // list as a unit and the group lock means "keep this list." The button is
  // disabled in the UI; this is the belt-and-suspenders guard.
  const handleRefreshSnippets = useCallback(async () => {
    if (!submit) return;
    if (isGroupLocked) return;

    const current = methods.getValues("details") as Ads.Asset[];
    const apiData = transformSnippets({ ...methods.getValues(), details: current });
    if (apiData) await saveNow(apiData);

    submit({
      intent: {
        type: "refresh_assets" as const,
        payload: {
          stage: "highlights",
          assets: [
            {
              asset: "structuredSnippets",
              nVariants: Ads.DefaultNumAssets.structuredSnippets,
            },
          ],
        },
        createdAt: new Date().toISOString(),
      },
    });
  }, [submit, methods, saveNow, isGroupLocked]);

  useFormRegistration("highlights", methods, getData, cancel);

  // Derive fields from RHF state so Controllers match form values after mutations
  const watchedDetails = methods.watch("details") as Ads.Asset[] | undefined;
  const fields = (watchedDetails?.length ? watchedDetails : details).map((d) => ({ ...d, id: d.id }));

  return (
    <div className="grid grid-cols-2">
      <div className="col-span-1">
        <FieldGroup className="gap-2">
          <div className="flex items-center gap-2 mb-6">
            <span className="font-semibold text-sm">Product or Service Offerings</span>
            <InfoTooltip text="Choose the category that best describes what you're listing. Google uses this to match your offerings to relevant searches." />
          </div>
          <Field>
            <FieldLabel className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs text-base-400">Category</span>
              </div>
            </FieldLabel>
            <Select
              value={methods.watch("category") || undefined}
              onValueChange={(value) => handleCategoryChange(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {STRUCTURED_SNIPPET_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <div className="flex justify-between items-center">
              <span className="font-semibold text-xs text-base-400">Details</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Select 3-10</Badge>
                <button
                  type="button"
                  onClick={handleGroupLockToggle}
                  className="p-1 hover:bg-neutral-100 rounded"
                  aria-label={isGroupLocked ? "Unlock snippet group" : "Lock snippet group"}
                  data-testid="snippet-group-lock"
                  data-locked={isGroupLocked}
                >
                  {isGroupLocked ? (
                    <Lock size={14} className="text-base" />
                  ) : (
                    <LockOpen size={14} className="text-[#96989B]" />
                  )}
                </button>
                <RefreshSuggestionsButton
                  onClick={handleRefreshSnippets}
                  disabled={isGroupLocked}
                />
              </div>
            </div>
          </Field>
          <AdCampaignFieldList
            fieldName="details"
            fields={fields as any}
            control={methods.control as any}
            placeholder="Detail value"
            maxLength={25}
            onDelete={handleDeleteDetail}
            onInputChange={handleInputChange}
            isPlaceholder={!hasSavedValues}
          />
          {methods.formState.errors.details?.message && (
            <p className="text-sm text-red-500">{methods.formState.errors.details.message as string}</p>
          )}
        </FieldGroup>
        <Button type="button" variant="ghost" size="sm" onClick={handleAddDetail}>
          <Plus /> Add Value
        </Button>
      </div>
    </div>
  );
}
